import React from "react";
import {Box, Text, Stack, PseudoBoxProps, PseudoBox} from "@chakra-ui/core";
import Image from "@pency/components/display/Image";

import {useTranslation} from "~/i18n/hooks";
import {Product} from "~/product/types";
import {usePrice} from "~/i18n/hooks";
import {getVariantsPriceRange} from "~/product/selectors";
import { formatImageUrl } from "~/product/utils/formatImage";
import ProductBadges from "~/product/components/ProductBadges";

interface Props extends Omit<PseudoBoxProps, "onClick"> {
  product: Product;
  onClick?: (product: Product) => void;
  isRaised?: boolean;
}

const PortraitProductCard: React.FC<Props> = ({isRaised = false, product, onClick, ...props}) => {
  const p = usePrice();
  const t = useTranslation();
  const {images, title, price, originalPrice, type, currentStock, handleStock} = product;
  const [min, max] = getVariantsPriceRange(product.options);

  function handleClick() {
    onClick && onClick(product);
  }
  let typeProduct = type;

  // If we get here by any point, return null
  if (type === "hidden") return null;
  if (handleStock && Number.isInteger(currentStock) && currentStock <= 0) typeProduct = "unavailable";
  return (
    <PseudoBox
      alignItems="flex-end"
      backgroundColor="page"
      borderColor="borderStrong"
      borderWidth={1}
      boxShadow={isRaised ? "lg" : "none"}
      cursor={onClick ? "pointer" : "inherit"}
      data-test-id="product"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      padding={{base: 2, sm: 3}}
      position="relative"
      rounded="md"
      transition="transform 0.2s"
      onClick={handleClick}
      {...props}
    >
      <Image
        fadeIn
        height={{base: 48, sm: 64}}
        rounded="md"
        src={formatImageUrl(images?.[0])}
        width="100%"
      />
      <Box
        display="flex"
        flex={1}
        flexDirection="column"
        height="100%"
        justifyContent="space-between"
        paddingTop={2}
        width="100%"
      >
        <Box marginBottom={2}>
          <ProductBadges badges={product.badges} marginBottom={1} />
          <Text
            display="block"
            fontSize="md"
            fontWeight={500}
            lineHeight="normal"
            overflowWrap="break-word"
          >
            {title}
          </Text>
        </Box>
        <Stack spacing={2}>
          {(type === "available" || type === "unavailable") && (
            <Stack isInline alignItems="center">
              <Text color="success" fontSize="sm" fontWeight={500} lineHeight={1}>
                {p(price)}
              </Text>
            </Stack>
          )}
          {type === "promotional" && (
            <Stack isInline alignItems="center">
              <Text color="success" fontSize="sm" fontWeight={500} lineHeight={1}>
                {p(price)}
              </Text>
              {originalPrice && (
                <Text color="subtle" fontSize="sm" lineHeight={1} textDecoration="line-through">
                  {p(originalPrice)}
                </Text>
              )}
            </Stack>
          )}
          {type === "variant" && (
            <Text color="success" fontSize="sm" fontWeight={500} lineHeight={1}>
              {min === max ? p(min) : `${p(min)} ~ ${p(max)}`}
            </Text>
          )}
          {type === "ask" && typeProduct !== "unavailable" && (
            <Text color="success" fontSize="sm" fontWeight={500} lineHeight={1}>
              {t("home.productStatus.ask")}
            </Text>
          )}
          {typeProduct === "unavailable" && (
            <Text color="yellow.500" fontSize="sm" fontWeight={500} lineHeight={1}>
              {t("home.productStatus.outStock")}
            </Text>
          )}
          {handleStock && Number.isInteger(currentStock) && currentStock > 0 && currentStock <= 5 && (
            <Text color="orange.500" fontSize="sm" fontWeight={500} lineHeight={1}>
              {`Últimas ${currentStock} disponibles`}
            </Text>
          )}
        </Stack>
      </Box>
    </PseudoBox>
  );
};

export default PortraitProductCard;
